import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AUTH_TOKEN, REACT_APP_API_PAM_URL, ORG_ID, DOMAIN_ID, REACT_APP_API_TENANTS_URL } from "../../../../constants";
import { logOutUser } from "../../../../common";
import axios from "axios";
import { Button, message } from "antd";
import { Download, Router } from "lucide-react";
import { AN_COLORS } from "../../../../design-system/tokens";
import { CodeBlock, InlineValue, Note } from "../../../../pages/enduser/ssh-proxy/sshProxyUi";
import { OnboardShell, PartHead, StepRow, card } from "./onboardUi";

const TUTORIAL =
  "https://customer-4qbnzcoswj67r0b2.cloudflarestream.com/65ef1a7012a0f0e98fd1484931df5726/watch";

// Commands are data rather than markup so each one has a single source — the
// old screen repeated every string twice, once to display and once to copy.
const AGENT_STEPS = [
  { title: "Download the installation script", cmd: "sudo wget https://github.com/authnull0/windows-endpoint/raw/linux-agent/agent/linux-agent/install_agent.sh -O install_agent.sh" },
  { title: "Make it executable", cmd: "sudo chmod +x install_agent.sh" },
  { title: "Run it", cmd: "sudo ./install_agent.sh" },
  { title: "Verify the install", cmd: "tail -f /var/log/authnull-agent.log", desc: "The agent logs here as it starts." },
];

const PAM_STEPS = [
  { title: "Download and extract the archive", cmds: ["sudo wget -O pam_authnull.tar https://github.com/authnull0/windows-endpoint/raw/refs/heads/main/pam_authnull.tar", "sudo tar -xvf pam_authnull.tar", "cd pam"] },
  { title: "Run the configure script", cmds: ["sudo chmod +x configure", "sudo ./configure"] },
  { title: "Install the PAM module", cmds: ["sudo make"] },
  { title: "Uninstall (optional)", cmds: ["sudo make uninstall"], desc: "To remove the PAM module later." },
];

export const OnboardLinuxV2 = ({ embedded = false, onDone }) => {
  const [notification, contextHolder] = message.useMessage();
  const [agentToken, setAgentToken] = useState("");
  const [orgId, setOrgId] = useState("");
  const [tenantId, setTenantId] = useState("");
  const [userId, setUserId] = useState("");
  const [uuid, setUuid] = useState("");
  const navigate = useNavigate();
  const [tenantDetails, setTenantDetails] = useState({});

  useEffect(() => {
    const Local_User_ID = localStorage.getItem("userId");
    const Local_Org_ID = localStorage.getItem("OrgId");
    const Local_Domain_ID = localStorage.getItem("DomainId");

    if (Local_User_ID) setUserId(Local_User_ID);
    if (Local_Org_ID) setOrgId(Local_Org_ID);
    if (Local_Domain_ID) setTenantId(Local_Domain_ID);
    fetchAgentKey();
    fetchTenantDetails();
    generateDynamicUuid();
  }, []);

  const generateEnvFileContent = () => {
// AWS_ACCESS_KEY_ID=${tenantDetails?.sessionrecordingkey}
// AWS_SECRET_ACCESS_KEY=${tenantDetails?.seesionrecordingsecret}
// AWS_REGION=${tenantDetails?.sessionrecordingregion}
// BUCKET_NAME=${tenantDetails?.sessionrecordingbucketname}
    
    const content = `KEY=LINUX02
MACHINE_KEY=LINUX02
AGENT_TOKEN=${agentToken}
ORG_ID=${orgId}
TENANT_ID=${tenantId}
USER_ID=${userId}
RECORDING_DIR=/anchor_dvr/
TEXT_RECORDING_DIR=/text_recordings/
FILE_NAME=test.guac
BUCKET_NAME_GCS=gto-did-app-dev
STORAGE_AWS_FLAG=true
INSTALL_SERVICES=false
UUID=${uuid}`;
    return content;
  };

  const generateDynamicUuid = () => {
    const characters = "abcdefghijklmnopqrstuvwxyz0123456789";
    let result = "";
    const sections = [8, 4, 4, 4, 12];
    sections.forEach((length) => {
      for (let i = 0; i < length; i++) {
        result += characters.charAt(Math.floor(Math.random() * characters.length));
      }
      result += "-";
    });
    result = result.slice(0, -1);
    setUuid(result);
  };

  const downloadEnvFile = () => {
    const content = generateEnvFileContent();
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "app.env";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    notification.success({ content: "app.env downloaded successfully." });
  };

  const fetchAgentKey = () => {
    let keyData = {
      domainId: DOMAIN_ID(),
      expired: false,
      orgId: ORG_ID(),
    };
    axios
      .post(`${REACT_APP_API_PAM_URL}/endpointUsers/generateAgentkey`, keyData, {
        headers: {
          withCredentials: true,
          "X-Authorization": AUTH_TOKEN(),
        },
      })
      .then((res) => {
        if (res.data) {
          setAgentToken(res.data.agentKey);
        }
      })
      .catch((err) => {
        if (err.response.data.message === "Invalid credentials" || err?.response?.status === 401) {
          notification.error({ content: "Credentials are invalid" });
          logOutUser();
        } else {
          notification.error({ content: "Unable to fetch agent key" });
        }
      });
  };

  const fetchTenantDetails = async () => {
    try {
      const tenantPayload = {
        email: localStorage.getItem("UserName"),
        tenantId: DOMAIN_ID(),
        orgId: ORG_ID(),
      };

      const response = await axios.post(
        `${REACT_APP_API_TENANTS_URL}/getTenantDetail`,
        tenantPayload,
        {
          headers: {
            withCredentials: true,
            "X-Authorization": AUTH_TOKEN(),
          },
        }
      );

      if (response && response.data && response.data.data) {
        setTenantDetails(response.data.data)
        return response.data.data;
      }
      return "";
    } catch (error) {
      console.error("Error fetching tenant details:", error?.response?.status, error?.message);
      notification.open({
        type: "error",
        content: "Unable to fetch tenant details",
      });
      return "";
    }
  };

  const finish = () => (onDone ? onDone() : navigate("/pam/endpoint"));

  return (
    <OnboardShell
      embedded={embedded}
      eyebrow="PAM · Endpoints · Linux"
      title="Add a Linux endpoint"
      subtitle="Install the AuthNull agent, then configure the PAM module so SSH logins are authenticated through Authnull."
      onClose={finish}
    >
      {contextHolder}

      {/* Part 1 — agent */}
      <div style={{ ...card, padding: 18 }}>
        <PartHead
          part="Part 1"
          title="Agent onboarding"
          desc="The agent runs on the machine and gives Authnull visibility of its local accounts."
          tutorialUrl={TUTORIAL}
        />

        <StepRow n={1} title="Download the configuration" desc="Contains the machine's own identifiers — the agent will not start without it.">
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <Button type="primary" onClick={downloadEnvFile} icon={<Download size={13} style={{ verticalAlign: -2, marginRight: 6 }} />}>
              Download app.env
            </Button>
            <span style={{ fontSize: 12, color: AN_COLORS.muted }}>
              Place it beside the installer as <InlineValue>app.env</InlineValue>.
            </span>
          </div>
        </StepRow>

        {AGENT_STEPS.map((st, idx) => (
          <StepRow key={st.title} n={idx + 2} title={st.title} desc={st.desc} last={idx === AGENT_STEPS.length - 1}>
            <CodeBlock copyText={st.cmd} multiline>{st.cmd}</CodeBlock>
          </StepRow>
        ))}

        {/* The gateway is set per endpoint on its detail page, not here: the
            agent registers the machine itself and never reads a gateway id. */}
        <div style={{ marginTop: 14 }}>
          <Note tone="info" icon={<Router size={15} />}>
            To reach this machine through an SSH gateway, open it after onboarding → <strong>Gateway Access</strong>.
          </Note>
        </div>
      </div>

      {/* Gateway detour */}
      <div style={{ ...card, padding: 18 }}>
        <Note tone="warn" icon={<Router size={15} />}>
          Setting this machine up as a <strong>gateway</strong> instead? Use the gateway onboarding and skip Part 2 below —
          a gateway authenticates operators at the gateway, so it does not need the PAM module.
          <div style={{ marginTop: 10 }}>
            <Button onClick={() => navigate("/pam/jumpServer/onboard")}>Onboard as a gateway</Button>
          </div>
        </Note>
      </div>

      {/* Part 2 — PAM module */}
      <div style={{ ...card, padding: 18 }}>
        <PartHead
          part="Part 2"
          title="PAM module configuration"
          desc="Puts Authnull in the SSH authentication path on this machine, so a login prompts for approval."
          tutorialUrl={TUTORIAL}
        />

        {PAM_STEPS.map((st, idx) => (
          <StepRow key={st.title} n={idx + 1} title={st.title} desc={st.desc}>
            <CodeBlock copyText={st.cmds.join("\n")} multiline>{st.cmds.join("\n")}</CodeBlock>
          </StepRow>
        ))}

        <StepRow n={PAM_STEPS.length + 1} title="Test the setup" desc="From your own machine, SSH to this host.">
          <ol style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, color: AN_COLORS.ink2, lineHeight: 1.7 }}>
            <li>Open a terminal on your local machine</li>
            <li>SSH to this endpoint</li>
            <li>You should see the AuthNull authentication prompt</li>
            <li>Approve it to gain access</li>
          </ol>
        </StepRow>

        <StepRow n={PAM_STEPS.length + 2} title="Monitor SSH logs" desc="Where to look when a login is refused." last>
          <CodeBlock copyText="tail -f /var/log/auth.log">tail -f /var/log/auth.log</CodeBlock>
        </StepRow>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Button type="primary" onClick={finish}>Finish</Button>
      </div>
    </OnboardShell>
  );
};
